import { z } from 'zod';

import { PUBLIC_PERSONAL_SHOPPER_AUTH_VERSION, PUBLIC_PRIVACY_VERSION, PUBLIC_TERMS_VERSION } from '../../../../shared/legalVersions';
import { API_PATHS } from '../../../lib/apiRoutes';
import { RECAPTCHA_SITE_KEY } from '../../../lib/env';
import { ApiError, apiRequest } from '../../../lib/api';
import { buildIdempotencyKey } from '../../../lib/idempotency';
import { validateWithSchema } from '../../shared/utils/apiValidation.util';
import type {
  AuthLoginRequest,
  AuthLoginResponse,
  AuthLogoutResponse,
  AuthRefreshResponse,
  AuthRegisterRequest,
  AuthRegisterResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  MeResponse,
  ResetPasswordRequest,
  ActiveSession
} from '../types/auth.types';

const nonEmptyStringSchema = z.string().trim().min(1);
const loginIdentifierSchema = z.string().trim().min(1, 'Email or username is required').max(254);
const usernameSchema = z
  .string()
  .trim()
  .min(3, 'Username must be at least 3 characters')
  .max(40, 'Username must be at most 40 characters')
  .regex(/^[a-zA-Z0-9_.-]+$/, 'Invalid username format');
const optionalRecaptchaSchema = z.string().trim().min(1).optional();

const loadRecaptchaScript = (siteKey: string): Promise<void> => {
  return new Promise((resolve) => {
    if (typeof document === 'undefined') {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src*="recaptcha/api.js"]');
    if (existing) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
};

type WindowWithRecaptcha = Window & {
  grecaptcha?: {
    execute?: (siteKey: string, options?: { action?: string }) => Promise<string>;
    ready?: (cb: () => void) => void;
  };
};

const waitForGrecaptcha = (timeoutMs = 5000): Promise<void> => {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }
    const start = Date.now();
    const check = () => {
      const w = window as WindowWithRecaptcha;
      if (w.grecaptcha?.execute && w.grecaptcha?.ready) {
        resolve();
      } else if (Date.now() - start > timeoutMs) {
        resolve(); // timeout fallback
      } else {
        setTimeout(check, 100);
      }
    };
    check();
  });
};

const readRecaptchaToken = async (): Promise<string | undefined> => {
  const w = window as WindowWithRecaptcha;

  if (!RECAPTCHA_SITE_KEY) return undefined;

  if (!w.grecaptcha?.execute || !w.grecaptcha?.ready) {
    await loadRecaptchaScript(RECAPTCHA_SITE_KEY);
    await waitForGrecaptcha();
  }

  if (!w.grecaptcha?.execute || !w.grecaptcha?.ready) return undefined;

  const executionPromise = new Promise<string | undefined>((resolve) => {
    try {
      w.grecaptcha?.ready?.(() => {
        w.grecaptcha?.execute?.(RECAPTCHA_SITE_KEY, { action: 'auth' })
          .then((token) => resolve(token || undefined))
          .catch(() => resolve(undefined));
      });
    } catch {
      resolve(undefined);
    }
  });

  const timeoutPromise = new Promise<string | undefined>((resolve) =>
    setTimeout(() => resolve(undefined), 2000)
  );

  return Promise.race([executionPromise, timeoutPromise]);
};

const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character');

const registerSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  username: usernameSchema,
  password: passwordSchema,
  role: z.enum(['customer', 'driver', 'affiliate']).optional(),
  acceptedTermsPrivacy: z.literal(true),
  termsVersion: z.literal(PUBLIC_TERMS_VERSION),
  privacyVersion: z.literal(PUBLIC_PRIVACY_VERSION),
  acceptedPersonalShopperAuthorization: z.literal(true).optional(),
  personalShopperAuthorizationVersion: z.literal(PUBLIC_PERSONAL_SHOPPER_AUTH_VERSION).optional(),
  recaptchaToken: optionalRecaptchaSchema
});

const loginSchema = z.object({
  email: loginIdentifierSchema,
  password: nonEmptyStringSchema,
  recaptchaToken: optionalRecaptchaSchema,
  mfaToken: z.string().trim().min(1).optional()
});

const forgotPasswordSchema = z.object({
  email: loginIdentifierSchema,
  recaptchaToken: optionalRecaptchaSchema
});

const verifyEmailSchema = z.object({
  token: nonEmptyStringSchema,
  recaptchaToken: optionalRecaptchaSchema
});

const resetPasswordSchema = z.object({
  token: nonEmptyStringSchema,
  password: passwordSchema,
  recaptchaToken: optionalRecaptchaSchema
});

const changePasswordSchema = z.object({
  currentPassword: nonEmptyStringSchema,
  newPassword: passwordSchema
});

const normalizeLoginIdentifier = (value: string) => {
  const trimmed = value.trim();
  if (trimmed.includes('@')) return trimmed.toLowerCase();
  return trimmed;
};

const withNormalizedLoginIdentifier = <T extends { email: string }>(payload: T): T => ({
  ...payload,
  email: normalizeLoginIdentifier(payload.email)
});

/** Feature API contract source of truth: docs/api/endpoints.md */
export const AUTH_API_CONTRACT = {
  register: API_PATHS.auth.register,
  login: API_PATHS.auth.login,
  refresh: API_PATHS.auth.refresh,
  tokenRefresh: API_PATHS.auth.tokenRefresh,
  session: API_PATHS.auth.session,
  logout: API_PATHS.auth.logout,
  me: API_PATHS.users.me,
  sendResetLink: API_PATHS.users.sendReset,
  forgotPassword: API_PATHS.auth.forgotPassword,
  resetPassword: API_PATHS.auth.resetPassword,
  verifyEmail: API_PATHS.auth.verifyEmail,
  resendVerification: API_PATHS.auth.resendVerification,
  mfaStatus: API_PATHS.auth.mfaStatus,
  mfaBegin: API_PATHS.auth.mfaBegin,
  mfaConfirm: API_PATHS.auth.mfaConfirm,
  mfaDisable: API_PATHS.auth.mfaDisable,
  mfaRegenerateBackupCodes: API_PATHS.auth.mfaRegenerateBackupCodes,
  sessionsIntrospect: API_PATHS.auth.sessionsIntrospect,
  sessionsRevokeAll: API_PATHS.auth.sessionsRevokeAll,
  sessionById: API_PATHS.auth.sessionById,
  changePassword: API_PATHS.auth.changePassword,
  stepUpConfirm: API_PATHS.auth.stepUpConfirm,
  phoneVerify: API_PATHS.auth.phoneVerify,
  requiredPayloadKeys: {
    register: [
      'email',
      'username',
      'acceptedTermsPrivacy',
      'termsVersion',
      'privacyVersion',
      'acceptedPersonalShopperAuthorization',
      'personalShopperAuthorizationVersion'
    ],
    login: ['email', 'password'],
    resetPassword: ['token', 'password'],
    verifyEmail: ['token'],
    resendVerification: ['email']
  }
} as const;

export const authApi = {
  register: async (payload: AuthRegisterRequest) => {
    const recaptchaToken = payload.recaptchaToken || await readRecaptchaToken();
    const normalizedPayload = {
      ...payload,
      email: payload.email.trim().toLowerCase(),
      recaptchaToken
    };
    return apiRequest<AuthRegisterResponse>(AUTH_API_CONTRACT.register, {
      method: 'POST',
      body: JSON.stringify(validateWithSchema(registerSchema, normalizedPayload, AUTH_API_CONTRACT.register))
    });
  },
  login: async (payload: AuthLoginRequest) => {
    const recaptchaToken = payload.recaptchaToken || await readRecaptchaToken();
    const normalizedPayload = withNormalizedLoginIdentifier({ ...payload, recaptchaToken });
    return apiRequest<AuthLoginResponse>(AUTH_API_CONTRACT.login, {
      method: 'POST',
      body: JSON.stringify(validateWithSchema(loginSchema, normalizedPayload, AUTH_API_CONTRACT.login))
    });
  },
  refresh: async () =>
    apiRequest<AuthRefreshResponse>(AUTH_API_CONTRACT.refresh, {
      method: 'POST',
      body: JSON.stringify({ recaptchaToken: await readRecaptchaToken() })
    }),
  refreshWithToken: async (refreshToken: string) =>
    apiRequest<AuthRefreshResponse>(AUTH_API_CONTRACT.tokenRefresh, {
      method: 'POST',
      body: JSON.stringify({ refreshToken, recaptchaToken: await readRecaptchaToken() })
    }),
  me: async () => {
    try {
      return await apiRequest<MeResponse>(AUTH_API_CONTRACT.session);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return apiRequest<MeResponse>(AUTH_API_CONTRACT.me);
      }
      throw error;
    }
  },
  logout: async () =>
    apiRequest<AuthLogoutResponse>(AUTH_API_CONTRACT.logout, {
      method: 'POST',
      body: JSON.stringify({ recaptchaToken: await readRecaptchaToken() })
    }),
  sendResetLink: (userId: string, payload?: ForgotPasswordRequest) => {
    const normalizedPayload = payload ? withNormalizedLoginIdentifier(payload) : undefined;
    return apiRequest<ForgotPasswordResponse>(AUTH_API_CONTRACT.sendResetLink(nonEmptyStringSchema.parse(userId)), {
      method: 'POST',
      body: JSON.stringify(normalizedPayload ? validateWithSchema(forgotPasswordSchema, normalizedPayload, AUTH_API_CONTRACT.sendResetLink(userId)) : {})
    });
  },
  forgotPassword: async (payload: ForgotPasswordRequest) => {
    const recaptchaToken = payload.recaptchaToken || await readRecaptchaToken();
    const normalizedPayload = withNormalizedLoginIdentifier({ ...payload, recaptchaToken });
    return apiRequest<ForgotPasswordResponse>(AUTH_API_CONTRACT.forgotPassword, {
      method: 'POST',
      body: JSON.stringify(validateWithSchema(forgotPasswordSchema, normalizedPayload, AUTH_API_CONTRACT.forgotPassword))
    });
  },
  resetPassword: async (payload: ResetPasswordRequest) =>
    apiRequest<void>(AUTH_API_CONTRACT.resetPassword, {
      method: 'POST',
      body: JSON.stringify(validateWithSchema(resetPasswordSchema, { ...payload, recaptchaToken: payload.recaptchaToken || await readRecaptchaToken() }, AUTH_API_CONTRACT.resetPassword))
    }),
  verifyEmail: async (token: string) => {
    const recaptchaToken = await readRecaptchaToken();
    return apiRequest<{ ok?: boolean }>(AUTH_API_CONTRACT.verifyEmail, {
      method: 'POST',
      body: JSON.stringify(
        validateWithSchema(verifyEmailSchema, { token: token.trim(), recaptchaToken }, AUTH_API_CONTRACT.verifyEmail)
      )
    });
  },
  resendVerification: async (email: string) => {
    const recaptchaToken = await readRecaptchaToken();
    return apiRequest<{ ok?: boolean }>(AUTH_API_CONTRACT.resendVerification, {
      method: 'POST',
      body: JSON.stringify({ email: email.trim(), recaptchaToken })
    });
  },
  confirmStepUp: async (password: string) => {
    const trimmed = String(password || '').trim();
    if (!trimmed) {
      throw new ApiError('Password is required', 400, { message: 'Password is required', code: 'VALIDATION_ERROR' });
    }
    return apiRequest<{ success: boolean; data?: { confirmedAt?: string; stepUpToken?: string } }>(AUTH_API_CONTRACT.stepUpConfirm, {
      method: 'POST',
      body: JSON.stringify({ password: trimmed }),
      idempotencyKey: buildIdempotencyKey('auth-step-up', [Date.now()]),
      requireIdempotencyKey: true
    });
  },
  verifyPhone: async (idToken: string) => {
    const trimmed = String(idToken || '').trim();
    if (!trimmed) {
      throw new ApiError('Firebase ID token is required', 400, {
        message: 'Firebase ID token is required',
        code: 'VALIDATION_ERROR'
      });
    }
    return apiRequest<{
      success: boolean;
      data?: { phoneNumber?: string; phoneVerified?: boolean; verifiedAt?: string };
    }>(AUTH_API_CONTRACT.phoneVerify, {
      method: 'POST',
      body: JSON.stringify({ idToken: trimmed }),
      idempotencyKey: buildIdempotencyKey('auth-phone-verify', [Date.now()]),
      requireIdempotencyKey: true
    });
  },
  mfaStatus: async () => {
    const response = await apiRequest<{ data?: { mfaEnabled?: boolean } }>(AUTH_API_CONTRACT.mfaStatus);
    return { mfaEnabled: Boolean(response?.data?.mfaEnabled) };
  },
  mfaBegin: async () => {
    const response = await apiRequest<{
      data?: {
        otpAuthUrl?: string;
        secret?: string;
        backupCodes?: string[];
        issuer?: string;
      };
    }>(AUTH_API_CONTRACT.mfaBegin, {
      method: 'POST',
      body: JSON.stringify({}),
      idempotencyKey: buildIdempotencyKey('mfa-begin')
    });
    const data = response?.data;
    return {
      otpAuthUrl: String(data?.otpAuthUrl || ''),
      secret: String(data?.secret || ''),
      backupCodes: Array.isArray(data?.backupCodes) ? data.backupCodes : [],
      issuer: String(data?.issuer || '')
    };
  },
  mfaConfirm: async (token: string) =>
    apiRequest<{ ok?: boolean }>(AUTH_API_CONTRACT.mfaConfirm, {
      method: 'POST',
      body: JSON.stringify({ token: nonEmptyStringSchema.parse(token.trim()) }),
      idempotencyKey: buildIdempotencyKey('mfa-confirm')
    }),
  mfaDisable: async (mfaToken?: string) =>
    apiRequest<{ ok?: boolean }>(AUTH_API_CONTRACT.mfaDisable, {
      method: 'POST',
      body: JSON.stringify(mfaToken?.trim() ? { mfaToken: mfaToken.trim() } : {}),
      idempotencyKey: buildIdempotencyKey('mfa-disable')
    }),
  mfaRegenerateBackupCodes: async (token: string) => {
    const response = await apiRequest<{ data?: { backupCodes?: string[] } }>(AUTH_API_CONTRACT.mfaRegenerateBackupCodes, {
      method: 'POST',
      body: JSON.stringify({ token: nonEmptyStringSchema.parse(token.trim()) }),
      idempotencyKey: buildIdempotencyKey('mfa-backup-regenerate')
    });
    return { backupCodes: Array.isArray(response?.data?.backupCodes) ? response.data.backupCodes : [] };
  },
  sessionIntrospect: async () => {
    const response = await apiRequest<{ data?: { activeSessions?: Array<Partial<ActiveSession>> } }>(
      AUTH_API_CONTRACT.sessionsIntrospect
    );
    const rows = Array.isArray(response?.data?.activeSessions) ? response.data.activeSessions : [];
    const activeSessions: ActiveSession[] = rows
      .filter((row) => typeof row.sessionId === 'string' && row.sessionId.length > 0)
      .map((row) => ({ ...row, sessionId: String(row.sessionId), current: row.current === true }));
    return { activeSessions };
  },
  revokeAllSessions: async () =>
    apiRequest<{ message?: string }>(AUTH_API_CONTRACT.sessionsRevokeAll, {
      method: 'POST',
      body: JSON.stringify({})
    }),
  revokeSession: async (sessionId: string) =>
    apiRequest<{ data?: { sessionId?: string } }>(AUTH_API_CONTRACT.sessionById(nonEmptyStringSchema.parse(sessionId)), {
      method: 'DELETE'
    }),
  /** Signs every device out on success; the caller must return the user to sign-in. */
  changePassword: async (payload: { currentPassword: string; newPassword: string }) =>
    apiRequest<{ data?: { message?: string } }>(AUTH_API_CONTRACT.changePassword, {
      method: 'POST',
      body: JSON.stringify(
        validateWithSchema(changePasswordSchema, payload, AUTH_API_CONTRACT.changePassword)
      ),
      idempotencyKey: buildIdempotencyKey('changePassword'),
      requireIdempotencyKey: true
    }),
  registerAgentKey: async (publicKey: string) => {
    const response = await apiRequest<{
      data?: { agentPublicKeyRegistered?: boolean; agentPublicKeyFingerprint?: string | null };
    }>(API_PATHS.users.agentKey, {
      method: 'POST',
      body: JSON.stringify({ publicKey: nonEmptyStringSchema.parse(publicKey) })
    });
    return {
      agentPublicKeyRegistered: Boolean(response?.data?.agentPublicKeyRegistered),
      agentPublicKeyFingerprint: response?.data?.agentPublicKeyFingerprint || null
    };
  }
};

